import * as React from 'react';
import injectSheet from 'react-jss';
import { withRouter } from 'react-router';
import moment from 'moment';
import { connect } from 'react-redux';
import { useGenericState } from 'src/Library/UseGenericState';
import { StylesProps } from 'src/Library/Types';
import { bindActionCreators } from 'redux';
import { updateBulkInquiryDeadline } from 'src/api/inquiry';
import { Button, DatePicker, message, Drawer, Icon, Tooltip} from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { buttonStyle } from "src/Library/CommonStyles";
import { getAllInquiriesApplyFilter } from 'src/actions/inquiryShipperActions';
import DisplaySelectedInquiries from './DisplaySelectedInquiries';
import { InquiryModes } from '../InquiryUtils';
import { tokens } from 'src/design-system';

const styles = (theme: any) => ({
  drawer: {
    '& .ant-drawer-header': {
      borderBottom: '1px solid #CCCCCC',
    },
    '& .ant-drawer-body': {
      padding: '30px',
    },
    '& .ant-drawer-content-wrapper': {
      minWidth: '500px',
    },
    '& .ant-drawer-title': {
      fontWeight: 500,
      fontSize: `${tokens.fontSize.h3}px`,
    },
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  modalTitle: {
    fontWeight: tokens.fontWeight.semibold,
    fontSize: `${tokens.fontSize.h1}px`,
    marginLeft: '16px',
  },
  closeIcon: {
    cursor: 'pointer',
    fontSize: `${tokens.fontSize.h1}px`
  },
  submitButton: {
    ...buttonStyle(tokens.colors.bgWhite, tokens.colors.primaryDark, '1px solid #006EC3'),
    padding: '0px 10px',
    borderRadius: `${tokens.radius.md}px`,
    marginLeft: '28px',
  },
  submitButtonDisabled: {
    ...buttonStyle(tokens.colors.bgWhite, '#CCCCCC', '1px solid #CCCCCC'),
    padding: '0px 10px',
    borderRadius: `${tokens.radius.md}px`,
    marginLeft: '28px',
    cursor: 'not-allowed',
  },
  datePickerSection: {
    paddingBottom: '20px',
    borderBottom: '1px solid #CCCCCC'
  },
  infoTextStyle: {
    paddingTop:'5px', 
    fontSize:'10px', 
    color:'#838383',
  },
})

interface IProps extends StylesProps<ReturnType<typeof styles>>, FormComponentProps {
  isVisible: boolean;
  milestonesLoading: boolean;
  onClose: (params: any) => void;
  selectedItems : Record<string, any>;
  onSubmit: (params: moment.Moment) => void;
  getAllInquiriesApplyFilter;
}

interface StateProps {
  isLoading?: boolean;
  isSubmitDisabled?: boolean;
  selectedDeadline?: moment.Moment;
  maxDeadline?: number;
  minSailingDate: number;
  isDeadlineSelected: boolean;
}

const ExtendBulkDeadlineModal = (props: IProps) => {

  const [state, setState] = useGenericState<StateProps>({
    isLoading: false,
    isSubmitDisabled: false,
    selectedDeadline: moment(),
    maxDeadline: 0,
    minSailingDate:  Number.MAX_SAFE_INTEGER,
    isDeadlineSelected: false,
  });

  const {
    isLoading,
    isSubmitDisabled,
    selectedDeadline,
    maxDeadline,
    minSailingDate,
    isDeadlineSelected,
  } = state;

  const {
    classes,
    isVisible,
    onClose,
    selectedItems,
    onSubmit,
  } = props;

  const totalInquiries = Object.keys(selectedItems)?.length;
  const MIN_TIME_DIFFERENCE = 5;
  const maxNameLength = 14;
  const DEADLINE_EXTENSION_FAILED_MESSAGE = "Deadline extension failed. Please try again later."

  React.useEffect(()=>{
    let maxDeadlineValue = 0;
    let minSailingDateValue = Number.MAX_SAFE_INTEGER
    Object.values(selectedItems).forEach(inquiry=>{
      maxDeadlineValue = Math.max(maxDeadlineValue, inquiry.deadline);
      minSailingDateValue = Math.min(minSailingDateValue, inquiry.mode === InquiryModes.TRAILER ? inquiry.cargoReadyDate : inquiry.preferredSailingEnddate)
    })
    setState({
      maxDeadline: maxDeadlineValue,
      minSailingDate: minSailingDateValue,
      selectedDeadline: moment(maxDeadlineValue),
      isSubmitDisabled:true
      })
  },[])

  const dateFormat = 'YYYY/MM/DD hh:mm a';
  
  const disabledDate = (current: moment.Moment) => {
    return current < moment(maxDeadline).startOf('day')
      || current > moment(minSailingDate).endOf('day') || current < moment().add(MIN_TIME_DIFFERENCE, 'minutes');
  }

  const handleSubmit = async() =>{
    if (moment.isMoment(selectedDeadline) && isValidDeadline(selectedDeadline)){
      setState({ isLoading: true })
      const inquiryIdList = Object.values(selectedItems).map(inquiry => inquiry.id)
      const updatedDeadline = {
        dateString: moment(selectedDeadline).format('YYYY-MM-DD'),
        time: moment(selectedDeadline).format('HH:mm'),
      }
      const params = {updatedDeadline, inquiryIdList}
      const response = await updateBulkInquiryDeadline(params);
      setState({isLoading: false})
      if (response.isSuccess) {
        message.success("Bulk Deadline extended successfully")
        onSubmit(selectedDeadline);
      } else {
        message.error(DEADLINE_EXTENSION_FAILED_MESSAGE)
        message.error(response.errorMessage)
      }
    } else if (moment(selectedDeadline).diff(moment(), 'minutes')<MIN_TIME_DIFFERENCE){
      message.error("New deadline should be at least 5 minutes from now")
    } else {
      message.error("Not a valid date")
    }
    onClose({});
  }

  const isValidDeadline = (currentDeadline) => {
    return (moment(currentDeadline).diff(moment(), 'minutes')>=MIN_TIME_DIFFERENCE) && !disabledDate(currentDeadline);
  }

  const disableSubmitButton = () => {
    return moment(maxDeadline).startOf('day') > moment(minSailingDate).endOf('day') || (Object.values(selectedItems).some(inquiry => inquiry?.status === 'CONFIRMED'));
  }

  const handleDatePickerChange = (currentDeadline) => {
    if (!isValidDeadline(currentDeadline)){
      setState({ isSubmitDisabled: true });
    } else {
      const disableSubmit = disableSubmitButton();
      setState({ isSubmitDisabled: disableSubmit});
    }
  }

  const renderTitle = () => {
    return (
      <div className={classes.modalHeader}>
        <div>
        <Icon type="close" className={classes.closeIcon} onClick={() => onClose({})} />
        <span className={classes.modalTitle}>Extend Deadline</span>
        </div>
        <Button
          className={isSubmitDisabled ? classes.submitButtonDisabled : classes.submitButton}
          onClick={() => {
            if (isSubmitDisabled) return;
            handleSubmit()
          }}
          loading={isLoading}
        >
          Submit
        </Button>
      </div>
    )
  }

  const setInitialDeadline = () => {
    if (isValidDeadline(selectedDeadline)){
      setState({isDeadlineSelected: true, isSubmitDisabled: false})
    } else if (isValidDeadline(moment().endOf('day'))){
      setState({selectedDeadline: moment().endOf('day'), isDeadlineSelected: true, isSubmitDisabled: false})
    }
  }

  const renderDatePicker = () => {
    return (
      <div className={classes.datePickerSection}>
        <div>
          <span style={{fontWeight:600, color:tokens.colors.text}}>New Deadline</span>
          <span 
          className={classes.infoTextStyle}
          style={{marginLeft:'10px', fontStyle:'italic'}}
          >This will be applied to all selected inquiries</span>
        </div> 
      <div style={{marginTop: '10px'}}>
      <DatePicker style={{ width: '70%' }}
          allowClear={false}
          format={dateFormat}
          disabledDate={disabledDate}
          showTime={  { defaultValue : moment('23:59:59', 'HH:mm:ss') } }
          placeholder={isDeadlineSelected? undefined :'Select New Deadline'}
          value={isDeadlineSelected?selectedDeadline: undefined}
          onOpenChange={()=> {setInitialDeadline()}}
          onChange={(currentDeadline) => {
            if (currentDeadline){
              if (isValidDeadline(currentDeadline)){
                setState({selectedDeadline: currentDeadline})
                handleDatePickerChange(currentDeadline)
              }
            }
          }}
          onOk={(currentDeadline) => {
            if (currentDeadline){
              setState({selectedDeadline: currentDeadline})
              handleDatePickerChange(currentDeadline)
            }
          }}
        />
        </div>
        <div className={classes.infoTextStyle}>
          <span> New deadline should be at least 5 minutes from now</span>
        </div>
      </div>
    )
  }

  return (
    <Drawer
    destroyOnClose={true}
    visible={isVisible}
    closable={false}
    onClose={() => onClose({})} 
    className={classes.drawer}
    title={renderTitle()}
    >
      {renderDatePicker()}
      <DisplaySelectedInquiries/>
  </Drawer>
  );
};

const mapStateToProps = ({inquiryShipperReducer}) => ({
  selectedItems : inquiryShipperReducer.selectedItems || {},
});
const mapDispatchToProps = dispatch => (bindActionCreators({
  getAllInquiriesApplyFilter,
}, dispatch));

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(ExtendBulkDeadlineModal)));
